import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { editModal, openModal, resetFormState, setSelectValidError, setRendererFormState } from "../../../../slices/common/reducer";
import { createQualification } from "../QualificationService";
import { setEditData } from "../../../../slices/qualification/qualificationsSlice";
import { renderElement } from "../../../../Components/Common/FormRendererV2/Renderer";
import { QualificationFieldsJSON } from "../Json/QualificationFieldsJson";
import { QualificationLayoutJSON } from "../Json/QualificationLayoutJson";
import { showToast } from "../../../../common/Toaster/Toaster";
import { getFilteredDataWithTabs } from "../../../../Utils/CommonService";

const NewQualificationModal = () => {
    const modalShow = useSelector((state) => state.common.modalShow);
    const edit = useSelector((state) => state.common.editShow);
    const editData = useSelector((state) => state.qualification.editData);
    const formState = useSelector((state) => state.common.formRendererState);
    const dispatch = useDispatch();

    const [qualFieldJson, setQualFieldJson] = useState(QualificationFieldsJSON);
    const [allData, setAllData] = useState([]);
    const [allAgsList, setAllAgsList] = useState([]);

    useEffect(() => {
        if (modalShow) {
            loadDropdowns();
        }
    }, [modalShow]);

    useEffect(() => {
        if (edit && editData && allData.length > 0 && allAgsList.length > 0) {
            dispatch(setRendererFormState({ key: "doctor_category", value: editData.doctor_category }));
            dispatch(setRendererFormState({ key: "qualification", value: editData.qualification }));

            const assignedAgs = allData
                .filter(item =>
                    item.qualification === editData.qualification
                    && item.doctor_category === editData.doctor_category
                    && item.ag_name
                )
                .map(item => item.ag_name);

            const allAgs = [...new Set(allAgsList.map(item => item.name).filter(Boolean))];
            const agString = allAgs.join("#");

            setQualFieldJson(prev => ({
                ...prev,
                "1001-customSelect": {
                    ...prev["1001-customSelect"],
                    "display-disabled": 1,
                },
                "1002-textfield": {
                    ...prev["1002-textfield"],
                    "display-disabled": 1,
                },
                "1003-multiselect": {
                    ...prev["1003-multiselect"],
                    "data-customvalue": agString,
                    "disabled-options": assignedAgs,
                },
            }));
        }
    }, [edit, editData, allData, allAgsList]);

    const loadDropdowns = async () => {
        try {
            // Load all Affinity Groups (AGRP)
            getFilteredDataWithTabs(
                "AGRP",
                (data) => {
                    if (data) {
                        setAllAgsList(data);
                        const agNames = data.map(ag => ag.name).filter(Boolean);
                        const uniqueAgs = [...new Set(agNames)];
                        const agString = uniqueAgs.join("#");
                        setQualFieldJson(prev => ({
                            ...prev,
                            "1003-multiselect": {
                                ...prev["1003-multiselect"],
                                "data-customvalue": agString,
                            },
                        }));
                    }

                    // Load existing qualifications sequentially after AGRP list is loaded
                    getFilteredDataWithTabs(
                        "QUAL",
                        (qualData) => {
                            if (qualData) {
                                setAllData(qualData);
                            }
                            return { type: "DUMMY_ACTION" };
                        },
                        {},
                        {
                            page: 0,
                            size: -1,
                            sort_column: "qualification",
                            sort_order: "DESC",
                            listEntityType: "QUAL"
                        },
                        "ALL"
                    );

                    return { type: "DUMMY_ACTION" };
                },
                {
                    enterpriseid: localStorage.getItem("enterpriseid")
                },
                {
                    page: 0,
                    size: -1,
                    listEntityType: "AGRP"
                },
                "ALL"
            );
        } catch (error) {
            console.log("Error loading dropdowns", error);
        }
    };

    const handleClose = () => {
        dispatch(openModal(false));
        dispatch(editModal(false));
        dispatch(setEditData(null));
        dispatch(resetFormState());
        setQualFieldJson(QualificationFieldsJSON);
    };

    const getAgIdByName = (name) => {
        // First try to find in the full AG list (AGRP)
        const agMatch = allAgsList.find(item => item.name === name);
        if (agMatch) {
            return agMatch.organizationid || agMatch.org_id || agMatch.entityid;
        }
        // Fallback to looking in the existing qualifications list
        const qualMatch = allData.find(item => item.ag_name === name);
        if (qualMatch) {
            return qualMatch.org_id;
        }
        // Last fallback to the name itself
        return name;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!edit) {
            const selectedAgs = Array.isArray(formState.organisation_id)
                ? formState.organisation_id
                : [formState.organisation_id].filter(Boolean);

            const duplicateAg = selectedAgs.find(agName =>
                allData.some(item =>
                    item.qualification?.toLowerCase() === formState.qualification?.toLowerCase()
                    && item.doctor_category === formState.doctor_category
                    && item.ag_name === agName
                )
            );

            if (duplicateAg) {
                showToast(`This qualification already exists for this category in AG: ${duplicateAg}!`, "error");
                return;
            }
        }

        if (formState.doctor_category && formState.qualification) {
            dispatch(setSelectValidError(false));
            const payload = {
                doctor_category: formState.doctor_category,
                qualification: formState.qualification,
                organisation_id: Array.isArray(formState.organisation_id)
                    ? formState.organisation_id.map(name => getAgIdByName(name))
                    : [getAgIdByName(formState.organisation_id)]
            };
            if (edit && editData) {
                payload.entityid = editData.entityid;
                payload.id = editData.entityid;
            }
            const res = await createQualification(payload);
            if (res) {
                handleClose();
            }
        } else {
            dispatch(setSelectValidError(true));
        }
    };

    return (
        <Modal
            isOpen={modalShow}
            onHide={() => handleClose()}
            centered
            className="border-0"
            modalClassName="modal fade zoomIn"
            toggle={handleClose}
            size="lg"
        >
            <Form className="tablelist-form" onSubmit={handleSubmit}>
                <ModalHeader className="p-3 bg-info-subtle" toggle={handleClose}>
                    {edit ? "Update Qualification" : "New Qualification"}
                </ModalHeader>
                <ModalBody className="modal-body p-4">
                    {QualificationLayoutJSON[0]?.child?.map((it) =>
                        renderElement(it, qualFieldJson, formState, {})
                    )}
                    <div className="hstack gap-2 justify-content-end mt-4">
                        <button
                            type="submit"
                            className="primary primary_button btn btn-primary"
                        >
                            {edit ? "Update" : "Save"}
                        </button>
                    </div>
                </ModalBody>
            </Form>
        </Modal>
    );
};

export default NewQualificationModal;